import { useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { socketManager } from 'utils/socketManager';
import { tradeOffersApi } from 'store/services/tradeOffers/tradeOffersApi';
import { tradeApi } from 'store/services/trade/tradeApi';
import { meApi } from 'store/services/user/meApi';
import { OfferUpdatePayload } from 'types/interfaces/tradeOffer';

interface UseOfferSocketOptions {
  enabled?: boolean;
}

// SocketProvider mounts outside the Router, so toast clicks navigate via
// window.location (the BadgeEarnedToast precedent).
const toastCopy = (
  payload: OfferUpdatePayload
): { message: string; destination: string } | null => {
  const { type, counterpartyUsername, offerId } = payload;
  switch (type) {
    case 'offer_received':
      return {
        message: `${counterpartyUsername} sent you a trade offer. Tap to review it.`,
        destination: `/trade-offers?offer=${offerId}`,
      };
    case 'offer_accepted':
      return {
        message: `${counterpartyUsername} accepted your trade offer!`,
        destination: `/trade-offers?offer=${offerId}`,
      };
    case 'offer_declined':
      return {
        message: `${counterpartyUsername} declined your trade offer.`,
        destination: '/trade-offers',
      };
    case 'offer_expired':
      return {
        message: `Your trade offer with ${counterpartyUsername} expired.`,
        destination: '/trade-offers',
      };
    case 'offer_failed':
      return {
        message: `Your trade offer to ${counterpartyUsername} couldn't be completed. A card was no longer available.`,
        destination: `/trade-offers?offer=${offerId}`,
      };
    default:
      return null;
  }
};

/**
 * Listens for the single discriminated offer:update event and switches on type
 * for toast copy and the invalidation set. Reconnect catch-up comes from tag
 * invalidation plus refetch-on-focus, same as the badge counts.
 */
export const useOfferSocket = (options: UseOfferSocketOptions = {}): void => {
  const { enabled = true } = options;
  const dispatch = useDispatch();
  const hasAcquiredRef = useRef(false);

  const handleOfferUpdate = useCallback(
    (payload: OfferUpdatePayload) => {
      console.log('[OfferSocket] Received offer:update', payload);
      queueMicrotask(() => {
        dispatch(
          tradeOffersApi.util.invalidateTags([
            'Offers',
            'CommittedQuantities',
            { type: 'Offer', id: payload.offerId },
          ])
        );
        if (payload.type === 'offer_accepted') {
          // Accepted offers created two trade legs and moved escrow
          dispatch(tradeApi.util.invalidateTags(['ActiveTrades', 'InboundTradeCount']));
          dispatch(meApi.util.invalidateTags(['User']));
        }
      });

      const copy = toastCopy(payload);
      if (copy) {
        toast.info(copy.message, {
          onClick: () => {
            window.location.href = copy.destination;
          },
        });
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (!enabled) return;

    hasAcquiredRef.current = true;
    const socket = socketManager.acquireSocket();

    socket.on('offer:update', handleOfferUpdate);

    return () => {
      socket.off('offer:update', handleOfferUpdate);

      if (hasAcquiredRef.current) {
        socketManager.releaseSocket();
        hasAcquiredRef.current = false;
      }
    };
  }, [enabled, handleOfferUpdate]);
};

export default useOfferSocket;
